import React from 'react'
import { Link, router } from '@inertiajs/react'
import PortfolioLayout from '@/layouts/PortfolioLayout'
import AccountSettingsNav from '@/components/AccountSettingsNav'
import { HelpTooltip } from '@/components/HelpTooltip'
import { useBrand } from '@/hooks/useBrand'
import type { AccountAbilities, AccountRole, AccountSettingsSection, CurrentAccount } from '@/types/shared'
import { Badge } from '@/components/ui/badge'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { CreditCard, ShieldCheck, CalendarClock, Sparkles, AlertTriangle, ExternalLink } from 'lucide-react'
import { formatMoney } from '@/lib/format'
import { apiFetch, apiErrorMessage } from '@/lib/csrf'
import CardCaptureDialog from '@/components/billing/CardCaptureDialog'
import { type PaymentMethodSummary } from '@/components/billing/PaymentMethodCard'
import PaymentMethodsSection, {
  type PaymentMethodHrefs,
  type PaymentMethodRow,
} from '@/components/billing/PaymentMethodsSection'
import PaymentStatusBadge from '@/components/billing/PaymentStatusBadge'
import BillingRoleNote from '@/components/billing/BillingRoleNote'
import InvoiceRow, { type InvoiceRowData } from '@/components/billing/InvoiceRow'
import TrialBanner from '@/components/billing/TrialBanner'
import GraceBanner from '@/components/billing/GraceBanner'
import LapsedBanner from '@/components/billing/LapsedBanner'

interface BillingPropertyRow {
  id: number
  name: string
  /**
   * This hotel's canonical path, served by Operator::BillingController#billing_property_rows.
   * The Billing page has no property in scope, so the row is the only thing that knows where its
   * own hotel lives — both the name link and the "Go live" CTA point at it.
   */
  basePath: string
  currency: string
  /**
   * The rooms this listing is billed on, server-resolved by role: a hotel's own rooms, a combined
   * inventory's lead listing the building's combined room count, and null for a following listing
   * (its rooms are on the lead's line; its monthly figure is the add-on), whose rooms column reads
   * "Add-on".
   */
  roomCount: number | null
  dryRun: boolean
  monthlyAmount: number | null
  /**
   * The listing's combined-inventory role in the operator's words, server-composed (null outside a
   * combined inventory). Optional so the page keeps rendering if the prop lags the frontend.
   */
  billingLabel?: string | null
  /**
   * THIS HOTEL'S OWN publishing answer (Property#billing_live_allowed?). Publishing follows the
   * payment method that settles the hotel, so a business whose second entity has lapsed keeps
   * publishing everywhere else — the page-level `liveAllowed` is only the worst-of summary for
   * the tile. Optional so the page keeps rendering if the prop lags the frontend.
   */
  liveAllowed?: boolean
  /** Which payment method settles this hotel — shown only when the business has more than one. */
  payerName?: string | null
  /**
   * The same fact as an id, for the Organize dialog to preselect. Server-resolved: the browser
   * never matches a hotel to a payment method by name.
   */
  payerId?: number | null
}

interface BillingProps {
  currentUser: { id: number; email: string }
  /**
   * The business this page is billing for. Its `settingsPath` is the root every billing endpoint
   * and every settings tab hangs a FIXED suffix off — this page has no property in scope and never
   * assembles the identity part of a URL. Always sent: BillingController 404s without an account.
   */
  currentAccount: CurrentAccount
  // Billing is now a tab of Business settings (/account/*). These arrive from the
  // backend alongside the other account pages' shared props — typed optional so the
  // page keeps rendering (without the tab nav) if the props lag the frontend.
  accountAbilities?: AccountAbilities
  currentRole?: AccountRole
  /**
   * Which settings tab is lit — server-decided (Operator::BillingController::BILLING_SECTION),
   * never parsed from the path. Always 'billing' here; typed as the union so this page cannot
   * invent a seventh tab. Optional for the same rollout reason as the two props above.
   */
  activeSection?: AccountSettingsSection
  subscriptionStatus: string
  // The ONE account-level plan name (derived backend-side from the active agreement's
  // catalog plan — plan-model refactor §7.3). Null = complimentary / no plan.
  plan: string | null
  liveAllowed: boolean
  marketIntelligenceEntitled: boolean
  currentPeriodEndsAt: string | null
  graceExpiresAt: string | null
  trialExpiresAt?: string | null
  accessFrozenAt?: string | null
  billingRows: BillingPropertyRow[]
  invoices: InvoiceRowData[]
  paymentMethod: PaymentMethodSummary | null
  /**
   * The business's payment methods, default first. One entry on every business that has never
   * been split, which is why this page renders exactly as it always did for them: one card block,
   * no plurality anywhere. Optional for the same rollout reason as the props above.
   */
  payers?: PaymentMethodRow[]
  /**
   * The self-serve management endpoints, served WHOLE by the backend (§9.4) rather than assembled
   * here — one of the three embeds a record id, and three sibling endpoints built two different
   * ways is how a straggler ships. Absent ⟹ the page renders read-only, exactly as it did before
   * self-serve existed.
   */
  paymentMethodHrefs?: PaymentMethodHrefs
  stripePublishableKey: string | null
}

// A stable identity for "the prop did not arrive", so the re-seeding effect below has a dependency
// that only changes when Inertia actually delivers different rows. A `= []` default in the
// signature mints a fresh array on every render, which would re-seed on every render forever.
const NO_PAYERS: PaymentMethodRow[] = []

// WHAT A PAYMENT CAN CHANGE BEYOND THE TWO LISTS ITS RESPONSE CARRIES (billing two clocks,
// 2026-10-05). Paying what is due can lift a pause: the strip, the access banner, the publishing
// tile and each hotel's own answer all move with it, and every one of them is composed by the
// backend. They are refetched by name; nothing is recomputed here.
const PAYMENT_DEPENDENT_PROPS = [
  'billingRows',
  'billingAlert',
  'subscriptionStatus',
  'liveAllowed',
  'graceExpiresAt',
  'accessFrozenAt',
]

// Where the invoice table starts. A payment method owing several invoices links its deadline
// sentence here (Operator::BillingController::INVOICES_ANCHOR serves the whole href); this page
// only gives the section the id.
const INVOICES_SECTION_ID = 'invoices'

// A backend ISO date rendered for display only — no client-side date math.
function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
}

export default function Billing({
  currentUser,
  currentAccount,
  accountAbilities,
  activeSection,
  subscriptionStatus,
  plan,
  liveAllowed,
  marketIntelligenceEntitled,
  currentPeriodEndsAt,
  graceExpiresAt,
  trialExpiresAt = null,
  accessFrozenAt = null,
  billingRows,
  invoices: invoicesProp,
  paymentMethod,
  payers: payersProp = NO_PAYERS,
  paymentMethodHrefs,
  stripePublishableKey,
}: BillingProps) {
  // Three mutually-exclusive access states, most-severe first (commercial model § 4 & 6):
  //   • Lapsed — access is frozen (trial-expiry OR payment past the 5-day grace). Rose banner.
  //   • Grace  — payment failed but still inside the grace window (not yet frozen). Amber banner.
  //   • Trial  — inside the one-time preview window. Teal banner.
  // `accessFrozenAt` is the single source of "frozen": it is set both when a trial expires and
  // when grace runs out, so it cleanly separates lapsed (rose) from in-grace (amber).
  const isLapsed = !!accessFrozenAt
  const inTrial = subscriptionStatus === 'trialing' && !isLapsed
  const inGrace = subscriptionStatus === 'past_due' && !!graceExpiresAt && !isLapsed

  const brand = useBrand()

  // The SetupIntent card-capture modal. Opening it mints a SetupIntent client_secret in-app (no
  // hosted Checkout redirect); the PaymentElement confirms the card off-session, and the dialog
  // then posts the confirmed intent to the synchronous vault and renders the card from the answer.
  // What it never does again is close on a blind reload that races the webhook (§11.1).
  const [cardModalOpen, setCardModalOpen] = React.useState(false)
  const [clientSecret, setClientSecret] = React.useState<string | null>(null)
  const [loadingSecret, setLoadingSecret] = React.useState(false)
  const [secretError, setSecretError] = React.useState<string | null>(null)
  // WHICH payment method the open modal is capturing a card for. The card must land on the entity
  // whose invoices it settles, so the id travels with the SetupIntent request and the backend
  // stamps it into the intent's metadata; null is the single-payment-method flow, where the
  // backend falls back to the business's own.
  const [cardPayer, setCardPayer] = React.useState<PaymentMethodRow | null>(null)

  // THE PAYMENT METHODS, AS THE SERVER LAST SAID THEM. Every self-serve write answers with the
  // rows re-read after it, so a save re-renders this section from the server's own state instead
  // of a full page load. `payersProp` re-seeds it whenever Inertia delivers a new page.
  const [payers, setPayers] = React.useState<PaymentMethodRow[]>(payersProp)
  React.useEffect(() => setPayers(payersProp), [payersProp])

  // THE INVOICES, ON THE SAME TERMS (billing-payers §10.2). Saving a grouping that moves money
  // DESTROYS and RE-ISSUES documents in that same request, and the response carries the new ones —
  // so the table below must be state, not a prop read straight through, or the operator would be
  // told "£75.00 moves to Harbour Annex Ltd" while the row above still showed the old document.
  const [invoices, setInvoices] = React.useState<InvoiceRowData[]>(invoicesProp)
  React.useEffect(() => setInvoices(invoicesProp), [invoicesProp])

  const multiplePayers = payers.length > 1

  // The grouping the Organize dialog opens with, held stable across re-renders. Mapping inline in
  // the JSX mints a fresh array every render, which re-fires the dialog's re-seeding effect — and
  // since §10.2 that effect also returns the dialog to its first step, so a stray re-render while
  // the operator was reading the money sentences would silently rewind them.
  const organizeHotels = React.useMemo(
    () => billingRows.map((row) => ({ id: row.id, name: row.name, payerId: row.payerId ?? null })),
    [billingRows],
  )

  async function openCardModal(payer: PaymentMethodRow | null = null) {
    setCardModalOpen(true)
    setClientSecret(null)
    setSecretError(null)
    setCardPayer(payer)
    setLoadingSecret(true)
    try {
      // Billing endpoints are account-scoped: `settingsPath` + a fixed suffix, backend-served.
      const res = await apiFetch(`${currentAccount.settingsPath}/billing/setup-payment-method`, {
        method: 'POST',
        body: JSON.stringify(payer ? { billing_profile_id: payer.id } : {}),
      })
      const data = await res.json()
      if (res.ok && data.clientSecret) {
        setClientSecret(data.clientSecret)
      } else {
        setSecretError(data.error ?? 'We couldn’t start the card form. Please try again.')
      }
    } catch (e) {
      setSecretError(apiErrorMessage(e, 'We couldn’t reach the payment service. Please try again.'))
    } finally {
      setLoadingSecret(false)
    }
  }

  // A saved grouping (or a renamed payment method) also changes what the two tables below print:
  // "Paid by" on each hotel, and on the invoices both the payment-method column and — since §10.2 —
  // the documents themselves. Every one of those strings is composed by the backend, so ONE RULE
  // decides where each comes from: the response is the source of truth for whatever it carries,
  // and a partial Inertia visit fetches exactly the rest. A grouping save carries fresh invoices
  // (it created them), so only `billingRows` is refetched; a rename carries neither, so both are.
  // Never a page reload, and never a value recomputed here.
  function refreshBillingTables(fresh?: { invoices?: InvoiceRowData[] }) {
    if (fresh?.invoices) setInvoices(fresh.invoices)
    router.reload({ only: fresh?.invoices ? ['billingRows'] : ['billingRows', 'invoices'] })
  }

  // AFTER A PAYMENT. The response carries the payment methods (already applied by the section) and
  // the invoices, which replace the table's own; the rest of what a payment can change is refetched
  // by name. A request that never reached a verdict carries neither list, so both are refetched too:
  // an earlier invoice may have been paid before the fault.
  function refreshAfterPayment(fresh?: { invoices?: InvoiceRowData[] }) {
    if (fresh?.invoices) setInvoices(fresh.invoices)
    router.reload({
      only: fresh?.invoices ? PAYMENT_DEPENDENT_PROPS : [...PAYMENT_DEPENDENT_PROPS, 'payers', 'invoices'],
    })
  }

  // THE CARD, FROM THE SERVER'S OWN ANSWER. The vault endpoint re-reads every payment method after
  // it saves, so the block that said "Add a card" a second ago now shows the card. No reload, and
  // therefore no race with the webhook that used to leave the operator staring at the old page.
  function onCardVaulted(fresh: PaymentMethodRow[]) {
    setPayers(fresh)
  }

  return (
    <PortfolioLayout title="Billing & plan" currentUser={currentUser}>
      <div className="mx-auto max-w-4xl space-y-6">
        {/* Heading */}
        <section className="animate-slide-in" style={{ animationDelay: '0ms' }}>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Billing</h1>
            <HelpTooltip content={`Your ${brand} subscription is billed per property by room count. Manage your card and view invoices here.`} />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Subscription, renewal, invoices and the per-property charges that make up your plan.
          </p>
        </section>

        {/* Business-settings tab nav — Billing reads as the "Billing & plan" tab. */}
        {accountAbilities && activeSection && (
          <AccountSettingsNav
            accountAbilities={accountAbilities}
            activeSection={activeSection}
            settingsPath={currentAccount.settingsPath}
          />
        )}

        {/* Access-state banner — teal (trial) · amber (grace) · rose (lapsed). Mutually exclusive. */}
        {(inTrial || inGrace || isLapsed) && (
          <section className="animate-slide-in" style={{ animationDelay: '50ms' }}>
            {isLapsed ? (
              <LapsedBanner />
            ) : inGrace ? (
              <GraceBanner graceEndsAtIso={graceExpiresAt} />
            ) : (
              <TrialBanner expiresAtIso={trialExpiresAt} />
            )}
          </section>
        )}

        {/* Status statement band */}
        <section className="animate-slide-in" style={{ animationDelay: '100ms' }}>
          <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <span className="text-sm font-semibold text-foreground">Subscription</span>
                <PaymentStatusBadge status={subscriptionStatus} />
              </div>
              {plan && (
                <span className="text-[11px] font-medium uppercase tracking-widest text-muted-foreground">
                  {plan} plan
                </span>
              )}
            </div>
            <dl className="grid grid-cols-1 divide-y divide-border/40 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
              <StatTile
                icon={<CalendarClock className="h-4 w-4 text-muted-foreground" />}
                // "paused", never "frozen" — `accessFrozenAt` is a column name, not a word an
                // operator reads. Same rule, same reason, as LapsedBanner's heading.
                label={isLapsed ? 'Access paused' : inGrace ? 'Grace ends' : inTrial ? 'Preview ends' : 'Renews on'}
                value={formatDate(isLapsed ? accessFrozenAt : inGrace ? graceExpiresAt : inTrial ? trialExpiresAt : currentPeriodEndsAt)}
              />
              <StatTile
                icon={<ShieldCheck className={`h-4 w-4 ${liveAllowed ? 'text-emerald-600' : 'text-rose-600'}`} />}
                label="Rate publishing"
                value={liveAllowed ? 'Allowed' : 'Paused'}
                valueClass={liveAllowed ? 'text-emerald-600' : 'text-rose-600'}
              />
              <StatTile
                icon={<Sparkles className={`h-4 w-4 ${marketIntelligenceEntitled ? 'text-primary' : 'text-muted-foreground/50'}`} />}
                label="Market intelligence"
                value={marketIntelligenceEntitled ? 'Included' : 'Not included'}
                valueClass={marketIntelligenceEntitled ? 'text-foreground' : 'text-muted-foreground'}
              />
            </dl>
          </div>
        </section>

        {/* Card management, and since §9 the operator's own management of the payment methods
            themselves. One block per payment method on a business that settles on several cards;
            on every other business this is the single card surface it has always been, plus one
            quiet line and one quiet button. No plurality noise, and no heading it did not have. */}
        <section className="animate-slide-in" style={{ animationDelay: '150ms' }}>
          <PaymentMethodsSection
            payers={payers}
            accountCard={paymentMethod}
            hrefs={paymentMethodHrefs}
            hotels={organizeHotels}
            onPayersChange={setPayers}
            onGroupingChanged={refreshBillingTables}
            onManageCard={openCardModal}
            onPaid={refreshAfterPayment}
          />
        </section>

        {/* Per-property line items — reads like an invoice */}
        <section className="animate-slide-in" style={{ animationDelay: '200ms' }}>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Properties on this plan</h2>
            {/* The single account-level plan indicator — the plan governs every row below
                (per-property tiers are retired). No plan = complimentary, never billed. */}
            {plan ? (
              <Badge variant="outline" className="border-primary/30 bg-primary/5 text-[11px] capitalize text-primary">
                {plan}
              </Badge>
            ) : (
              <Badge variant="outline" className="border-border bg-muted text-[11px] text-muted-foreground">
                Complimentary
              </Badge>
            )}
            <HelpTooltip content={`Each property is billed by its room count on your plan's rates. The monthly amount is calculated by ${brand} and shown here exactly as it bills.`} />
          </div>

          {billingRows.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <CreditCard className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No properties yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Connect a property to start your subscription.</p>
              <Link
                href="/onboarding"
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-foreground px-4 py-2 text-xs font-medium text-white transition-colors hover:bg-foreground/90"
              >
                Add property
              </Link>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    {multiplePayers && <TableHead>Paid by</TableHead>}
                    <TableHead className="text-right">Rooms</TableHead>
                    <TableHead className="text-right">Monthly</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {billingRows.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <Link href={p.basePath} className="font-medium text-foreground hover:text-primary transition-colors">
                          {p.name}
                        </Link>
                        <BillingRoleNote label={p.billingLabel} />
                      </TableCell>
                      {multiplePayers && (
                        <TableCell className="text-sm text-muted-foreground">{p.payerName ?? '—'}</TableCell>
                      )}
                      <TableCell className="text-right tabular-nums text-sm text-muted-foreground">{p.roomCount ?? 'Add-on'}</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold text-foreground">
                        {p.monthlyAmount === null ? '—' : `${formatMoney(p.monthlyAmount, p.currency)}/mo`}
                      </TableCell>
                      <TableCell className="text-right">
                        {/* The ROW's own answer when the backend sends one — publishing follows the
                            payment method that settles this hotel, not the worst one on the
                            business. Falls back to the page-level value if the prop lags. */}
                        <GoLiveState propertyBasePath={p.basePath} dryRun={p.dryRun} liveAllowed={p.liveAllowed ?? liveAllowed} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        {/* Invoice history (in-house). The id is the target a payment method owing several invoices
            links to; scroll-mt clears the sticky header and the billing strip riding in it. */}
        <section id={INVOICES_SECTION_ID} className="animate-slide-in scroll-mt-40" style={{ animationDelay: '250ms' }}>
          <div className="mb-2 flex items-center gap-2">
            <h2 className="text-sm font-semibold text-foreground">Invoices</h2>
            <HelpTooltip content={`Your billing history. ${brand} issues each invoice in GBP; amounts include any applicable VAT. A failed payment can be retried against your card on file.`} />
          </div>

          {invoices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center shadow-sm">
              <CalendarClock className="mx-auto h-8 w-8 text-muted-foreground/40" />
              <p className="mt-3 text-sm font-medium text-foreground">No invoices yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Your first invoice will appear after your first billing period.</p>
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice</TableHead>
                    <TableHead>Period</TableHead>
                    {/* One whole invoice per payment method, so on a business with more than one
                        the number alone does not say whose card settles it. */}
                    {multiplePayers && <TableHead>Payment method</TableHead>}
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                    <TableHead className="text-right" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <InvoiceRow
                      key={invoice.id}
                      invoice={invoice}
                      settingsPath={currentAccount.settingsPath}
                      showPayer={multiplePayers}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>

      {/* In-app card capture (SetupIntent + PaymentElement + the synchronous vault) — no hosted
          Checkout redirect, and no path that ends without the operator being told something. */}
      <CardCaptureDialog
        open={cardModalOpen}
        onOpenChange={setCardModalOpen}
        payer={cardPayer}
        multiplePayers={multiplePayers}
        accountCard={paymentMethod}
        confirmHref={paymentMethodHrefs?.confirmCard}
        clientSecret={clientSecret}
        loadingSecret={loadingSecret}
        secretError={secretError}
        stripePublishableKey={stripePublishableKey}
        onSaved={onCardVaulted}
      />
    </PortfolioLayout>
  )
}

function StatTile({
  icon,
  label,
  value,
  valueClass = 'text-foreground',
}: {
  icon: React.ReactNode
  label: string
  value: string
  valueClass?: string
}) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-base font-semibold tabular-nums ${valueClass}`}>{value}</div>
    </div>
  )
}

// Per-property go-live indicator. A property already live shows a quiet "Live" badge. A dry-run
// property links to its dashboard (where go-live is initiated) only when billing allows
// publishing; otherwise it shows a disabled "Billing required" state. Navigation uses <Link>,
// never a router.visit button.
function GoLiveState({ propertyBasePath, dryRun, liveAllowed }: { propertyBasePath: string; dryRun: boolean; liveAllowed: boolean }) {
  if (!dryRun) {
    return (
      <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[11px] text-emerald-700">
        Live
      </Badge>
    )
  }

  if (!liveAllowed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-lg border border-border/60 bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">
        <AlertTriangle className="h-3 w-3" />
        Billing required
      </span>
    )
  }

  return (
    <Link
      href={propertyBasePath}
      className="inline-flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2.5 py-1 text-[11px] font-medium text-primary transition-colors hover:bg-primary/10"
    >
      Go live
      <ExternalLink className="h-3 w-3" />
    </Link>
  )
}
